import { DisposableStack, disposeSymbol } from '@mui/x-internals/disposable';
import type { TemporalSupportedObject } from '@mui/x-scheduler-internals/models';
import type { Adapter } from '@mui/x-scheduler-internals/use-adapter';
import { TimeoutManager } from '@mui/x-scheduler-internals/internals';

const MAX_CONCURRENT_REQUESTS = 3;
const MAX_QUEUED_REQUESTS = 3;
export const DEBOUNCE_MS = 150;

export enum RequestStatus {
  QUEUED,
  PENDING,
  SETTLED,
  UNKNOWN,
}

export interface DateRange {
  start: TemporalSupportedObject;
  end: TemporalSupportedObject;
}

/**
 * Generates a unique key for a date range using timestamps.
 * Format: "startTimestamp:endTimestamp"
 */
function getDateRangeKey(adapter: Adapter, range: DateRange): string {
  const startTimestamp = adapter.getTime(range.start);
  const endTimestamp = adapter.getTime(range.end);
  return `${startTimestamp}:${endTimestamp}`;
}

/**
 * Fetches events from the data source with option to limit the number of concurrent requests.
 * Determines the status of a request based on the enum `RequestStatus`.
 * Uses date range keys to uniquely identify a request.
 *
 * Features:
 * - Debounces rapid successive requests
 * - Limits queued requests to prevent queue bloat
 * - Prioritizes the most recently queued request
 * - Skips already settled requests
 */
export class SchedulerDataManager {
  private pendingRequests: Map<string, DateRange> = new Map();

  private queuedRequests: Map<string, DateRange> = new Map();

  private settledRequests: Set<string> = new Set();

  declare private adapter: Adapter;

  declare private maxConcurrentRequests: number;

  declare private maxQueuedRequests: number;

  declare private debounceMs: number;

  private readonly disposables = new DisposableStack();

  // Registered first so `clearAll` runs last (LIFO) — the constructor's defer
  // calls `cancelQueuedRequests`, which itself touches `timeoutManager`.
  private timeoutManager = this.disposables.use(new TimeoutManager());

  // Requests waiting for the debounce timer to finish (rapid navigation buffer)
  private stagedRanges: DateRange[] | null = null;

  private pendingDebounceResolve: (() => void) | null = null;

  declare private fetchFunction: (range: DateRange, adapter: Adapter) => Promise<void>;

  public get disposed(): boolean {
    return this.disposables.disposed;
  }

  constructor(
    adapter: Adapter,
    fetchFunction: (range: DateRange, adapter: Adapter) => Promise<void>,
    options: {
      maxConcurrentRequests?: number;
      maxQueuedRequests?: number;
      debounceMs?: number;
    } = {},
  ) {
    this.adapter = adapter;
    this.fetchFunction = fetchFunction;
    this.maxConcurrentRequests = options.maxConcurrentRequests ?? MAX_CONCURRENT_REQUESTS;
    this.maxQueuedRequests = options.maxQueuedRequests ?? MAX_QUEUED_REQUESTS;
    this.debounceMs = options.debounceMs ?? DEBOUNCE_MS;

    this.disposables.defer(() => {
      this.cancelQueuedRequests();
      this.pendingRequests.clear();
      this.settledRequests.clear();
    });
  }

  /**
   * Helper to safely add ranges to the queue and enforce limits.
   */
  private commitRangesToQueue = (ranges: DateRange[]) => {
    ranges.forEach((range) => {
      const key = getDateRangeKey(this.adapter, range);

      // Skip if already settled, currently fetching, or already in queue
      if (this.pendingRequests.has(key) || this.queuedRequests.has(key)) {
        return;
      }

      this.queuedRequests.set(key, range);
    });
    // Trim queue if it exceeds max size.
    while (this.queuedRequests.size > this.maxQueuedRequests) {
      const firstKey = this.queuedRequests.keys().next().value;
      if (firstKey !== undefined) {
        this.queuedRequests.delete(firstKey);
      }
    }
  };

  private processQueue = async () => {
    if (this.queuedRequests.size === 0 || this.pendingRequests.size >= this.maxConcurrentRequests) {
      return;
    }

    const loopLength = Math.min(
      this.maxConcurrentRequests - this.pendingRequests.size,
      this.queuedRequests.size,
    );

    if (loopLength === 0) {
      return;
    }

    const fetchQueue = Array.from(this.queuedRequests.entries());
    const fetchPromises: Promise<void>[] = [];

    const startIndex = Math.max(0, fetchQueue.length - loopLength);
    const itemsToProcess = fetchQueue.slice(startIndex);

    for (const [rangeKey, range] of itemsToProcess) {
      this.queuedRequests.delete(rangeKey);
      this.pendingRequests.set(rangeKey, range);

      fetchPromises.push(this.fetchFunction(range, this.adapter));
    }

    await Promise.all(fetchPromises);
  };

  /**
   * The returned promise resolves when the debounce window flushes, NOT when this
   * specific call's data has been fetched. If a subsequent `queue()` arrives within
   * the debounce window, the previous promise resolves immediately and the new call
   * takes over — callers shouldn't treat the resolution as a fetch-completion signal.
   */
  public queue = async (ranges: DateRange[]) => {
    if (this.disposed) {
      return undefined;
    }
    if (this.pendingDebounceResolve) {
      this.pendingDebounceResolve();
      this.pendingDebounceResolve = null;
    }
    this.timeoutManager.clearTimeout('debounce');

    this.stagedRanges = [...ranges];

    return new Promise<void>((resolve, reject) => {
      this.pendingDebounceResolve = resolve;
      this.timeoutManager.startTimeout('debounce', this.debounceMs, async () => {
        this.pendingDebounceResolve = null;
        // Move from Stage -> Actual Queue
        if (this.stagedRanges) {
          this.commitRangesToQueue(this.stagedRanges);
          this.stagedRanges = null;
        }

        try {
          await this.processQueue();
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  };

  /**
   * Immediately processes the queue without debouncing.
   * Useful for initial load or forced refresh.
   */
  public queueImmediate = async (ranges: DateRange[]) => {
    if (this.disposed) {
      return;
    }
    // Clear any pending debounce
    this.timeoutManager.clearTimeout('debounce');
    if (this.pendingDebounceResolve) {
      this.pendingDebounceResolve();
      this.pendingDebounceResolve = null;
    }

    this.stagedRanges = null;

    this.commitRangesToQueue(ranges);
    await this.processQueue();
  };

  public cancelQueuedRequests = () => {
    // Clear any pending debounce
    this.timeoutManager.clearTimeout('debounce');
    if (this.pendingDebounceResolve) {
      this.pendingDebounceResolve();
      this.pendingDebounceResolve = null;
    }

    this.stagedRanges = null;
    this.queuedRequests.clear();
  };

  public setRequestSettled = async (range: DateRange) => {
    if (this.disposed) {
      return;
    }
    const key = getDateRangeKey(this.adapter, range);
    this.pendingRequests.delete(key);
    this.settledRequests.add(key);
    await this.processQueue();
  };

  [disposeSymbol](): void {
    this.disposables.dispose();
  }

  public clearPendingRequest = async (range: DateRange) => {
    const key = getDateRangeKey(this.adapter, range);
    this.pendingRequests.delete(key);
    await this.processQueue();
  };

  public getRequestStatus = (range: DateRange) => {
    const key = getDateRangeKey(this.adapter, range);

    if (this.pendingRequests.has(key)) {
      return RequestStatus.PENDING;
    }
    if (this.queuedRequests.has(key)) {
      return RequestStatus.QUEUED;
    }
    if (this.settledRequests.has(key)) {
      return RequestStatus.SETTLED;
    }
    return RequestStatus.UNKNOWN;
  };

  public getActiveRequestsCount = () => this.pendingRequests.size + this.queuedRequests.size;
}
